package dev.lingvo.site.pages.app.settings.tokens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableAlign
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One API token: what it may do, and what it has actually been doing.
 *
 * **The denied column is the reason to open this page.** A token being refused over and over is
 * either a script looping on a permission it will never get, or somebody probing — and both are
 * invisible in a list that only counts successes.
 *
 * Routes are shown as **patterns**, never as concrete paths: `/projects/{slug}/keys` rather than
 * `/projects/acme-checkout/keys`. A usage log full of real slugs is a second copy of the project
 * list, readable by anybody who gets this page onto a screen.
 */
@Page("/app/settings/tokens/{tokenId}")
@Layout(".components.layouts.AppLayout")
@Composable
fun TokenDetailPage() {
    val ctx = rememberPageContext()
    val tokenId = ctx.route.params["tokenId"].orEmpty()
    val scope = rememberCoroutineScope()

    var refreshKey by remember { mutableStateOf(0) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var confirmingRevoke by remember { mutableStateOf(false) }

    val tokens = loadAsync(refreshKey) { ApiClient.listApiTokens() }
    val usage = loadAsync(tokenId, refreshKey) { ApiClient.tokenUsage(tokenId) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        LAsyncContent(
            tokens,
            rows = 3,
            loadingLabel = t("app.settings.tokens.token_id.loading_the_token", "Loading the token"),
            onRetry = { refreshKey++ },
            gap = Space.XL,
        ) { allTokens ->
            val token = allTokens.orEmpty().firstOrNull { it.id == tokenId }
            if (token == null) {
                PageHeader(t("app.settings.tokens.token_id.token", "Token"), "")
                LAlert(
                    t("app.settings.tokens.token_id.this_token_is_gone", "This token is gone"),
                    t("app.settings.tokens.token_id.it_was_revoked_or_it_belongs_2", "It was revoked, or it belongs to somebody else. Tokens are personal: you " +
                        "only ever see your own."),
                    variant = LAlertVariant.INFO,
                )
                Link(path = "/app/settings/tokens") { Text(t("app.settings.tokens.token_id.back_to_tokens", "Back to tokens")) }
                return@LAsyncContent
            }

            PageHeader(token.name, t("app.settings.tokens.token_id.what_this_credential_may_do_and", "What this credential may do, and what it has been doing."))
            // Appears only when the revoke this page offers has failed, so it is announced —
            // the button that produced it is the last thing a reader touched.
            message?.let {
                LAlert(
                    t("app.settings.tokens.token_id.that_didnt_work", "That didn't work"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                        Span(Modifier.flexGrow(1).monoFont().toAttrs()) { Text("${token.prefix}…") }  // i18n-ignore — a token's first characters and an ellipsis, which is a shape rather than a sentence
                        if (token.isLegacy) LBadge(t("app.settings.tokens.token_id.legacy", "legacy"), variant = LBadgeVariant.WARNING)
                    }
                    Row(Modifier.fillMaxWidth().gap(Space.XS)) {
                        token.scopes.forEach { LBadge(it.id) }
                    }
                    token.scopes.forEach { LMuted(tf("app.settings.tokens.token_id.scope_and_consequence", "{scope} — {consequence}", "scope" to it.id, "consequence" to it.consequence)) }
                    LMuted(
                        if (token.projectSlugs.isEmpty()) {
                            t("app.settings.tokens.token_id.every_project_you_can_reach", "Every project you can reach.")
                        } else {
                            trf(
                        "app.settings.tokens.token_id.only_projects",
                        "Only: {projects}",
                        "projects" to token.projectSlugs.joinToString(", "),
                    )
                        },
                    )
                    LMuted(
                        token.expiresAt?.let {
                            trf(
                                "app.settings.tokens.token_id.expires_date",
                                "Expires {date}.",
                                "date" to it.take(10),
                            )
                        }
                            // An honest label rather than a reassuring silence: this is the
                            // state the expiry warnings exist to move people off.
                            ?: t(
                                "app.settings.tokens.token_id.never_expires",
                                "Never expires. Newer tokens do.",
                            ),
                    )
                    LMuted(
                        token.lastUsedAt?.let {
                            trf(
                                "app.settings.tokens.token_id.last_used_date",
                                "Last used {date}.",
                                "date" to it.take(10),
                            )
                        } ?: t("app.settings.tokens.token_id.never_used_yet", "Never used yet."),
                    )
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.tokens.token_id.what_it_has_been_doing", "What it has been doing"))
                    LAsyncContent(
                        usage,
                        rows = 3,
                        loadingLabel = t("app.settings.tokens.token_id.loading_usage", "Loading usage"),
                        onRetry = { refreshKey++ },
                    ) { usageRows ->
                        val rows = usageRows.orEmpty()
                        if (rows.isEmpty()) {
                            LEmptyState(
                                t("app.settings.tokens.token_id.nothing_yet", "Nothing yet"),
                                t("app.settings.tokens.token_id.this_token_has_not_been_used_2", "This token has not been used. If a script should be using it, " +
                                    "check that it is reading LINGVO_TOKEN from the environment."),
                                Modifier.fillMaxWidth(),
                                LEmptyStateIcon.AUDIT,
                            ) {}
                        } else {
                            val refused = rows.sumOf { it.denied }
                            if (refused > 0) {
                                LAlert(
                                    // A count, and the verb moves with it. Hard-coding the
                                    // plural form said "1 calls were refused" on the single
                                    // refusal that is most worth reading, and left the
                                    // sentence outside the catalogue entirely.
                                    tPlural(
                                        "app.settings.tokens.token_id.calls_refused",
                                        refused,
                                        "{count} call was refused",
                                        "{count} calls were refused",
                                    ),
                                    t("app.settings.tokens.token_id.a_token_refused_repeatedly_is_either_2", "A token refused repeatedly is either a script looping on a " +
                                        "permission it will never get, or somebody probing. " +
                                        "Both are worth a look at the routes below."),
                                    variant = LAlertVariant.WARNING,
                                )
                            }
                            LTable(caption = t("app.settings.tokens.token_id.calls_by_route", "Calls by route")) {
                                LTableHead {
                                    LTableRow {
                                        LTableHeaderCell { Text(t("app.settings.tokens.token_id.column_route", "Route")) }
                                        listOf(
                                            t("app.settings.tokens.token_id.column_calls", "Calls"),
                                            t("app.settings.tokens.token_id.column_refused", "Refused"),
                                        ).forEach { LTableHeaderCell(align = LTableAlign.END) { Text(it) } }
                                        LTableHeaderCell { Text(t("app.settings.tokens.token_id.column_last_used", "Last used")) }
                                    }
                                }
                                LTableBody {
                                    rows.forEach { row ->
                                        LTableRow {
                                            LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                                                P(Modifier.monoFont().margin(0.px).toAttrs()) {
                                                    Text(row.route)
                                                }
                                            }
                                            Number(row.calls)
                                            LTableCell(align = LTableAlign.END) {
                                                if (row.denied > 0) {
                                                    LBadge(
                                                        row.denied.toString(),
                                                        variant = LBadgeVariant.WARNING,
                                                    )
                                                } else {
                                                    P(Modifier.tabularNumbers().margin(0.px).toAttrs()) {
                                                        Text("0")
                                                    }
                                                }
                                            }
                                            LTableCell { Text(formatUtc(row.lastUsedAt, LTimeStyle.AGE, absent = t("app.settings.tokens.token_id.never_used", "Never used"))) }
                                        }
                                    }
                                }
                            }
                            LMuted(t("app.settings.tokens.token_id.route_patterns_never_the_projects_they", "Route patterns, never the projects they touched."))
                        }
                    }
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.tokens.token_id.revoke", "Revoke"))
                    Row(Modifier.gap(Space.SM)) {
                        LButton(
                            t("app.settings.tokens.token_id.revoke_this_token", "Revoke this token"),
                            { confirmingRevoke = true },
                            variant = LButtonVariant.DANGER,
                            enabled = !busy,
                        )
                    }
                    // There is no rotation here, and saying so beats a button that mints a
                    // second credential and leaves the first one working.
                    LMuted(
                        t("app.settings.tokens.token_id.there_is_no_rotate_create_the_2", "There is no rotate: create the replacement, point your scripts at it, " +
                            "then revoke this one. Two live tokens for an afternoon is safer than " +
                            "a rotation that silently keeps the old one working."),
                    )
                }
            }
            // Arming and firing shared one button, and nothing ever wrote `confirmingRevoke` back
            // to false: a reader who armed this and thought better of it held a live
            // "Yes, revoke it" until they reloaded the page. Both siblings retired that pattern
            // (`Agents.kt`, `agents/[agentId].kt`), and the token list already opens a dialog for
            // this exact action.
            LConfirmDialog(
                open = confirmingRevoke,
                title = t("app.settings.tokens.token_id.revoke_api_token", "Revoke API token?"),
                confirmLabel = t("app.settings.tokens.token_id.revoke_token", "Revoke token"),
                onConfirm = {
                    scope.launch {
                        busy = true
                        message = settingsOperationResult { ApiClient.deleteApiToken(token.id) }
                            .fold(
                                { ctx.router.navigateTo("/app/settings/tokens"); null },
                                { it.message },
                            )
                        busy = false
                        // Closed on failure too: this page's own account of what went wrong sits
                        // behind the scrim.
                        confirmingRevoke = false
                    }
                },
                onCancel = { confirmingRevoke = false },
                consequenceTitle = t("app.settings.tokens.token_id.access_will_stop_immediately", "Access will stop immediately"),
                consequence = trf(
                    "app.settings.tokens.token_id.token_will_no_longer_authenticate",
                    "{name} ({prefix}…) will no longer authenticate CLI or API requests. Anything " +
                        "still using it starts getting 401 on its next call.",
                    "name" to token.name,
                    "prefix" to token.prefix,
                ),
                busy = busy,
            )
            Link(path = "/app/settings/tokens") { Text(t("app.settings.tokens.token_id.back_to_tokens", "Back to tokens")) }
        }
    }
}

@Composable
private fun Number(value: Int) {
    // Grouped, for the reason `usage/Events.kt` states beside its own copy of this helper: a
    // five-digit count is misread without it.
    LTableCell { P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text(formatCount(value)) } }
}

